import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faUsers,
    faBuilding,
    faStore,
    faUserTie,
    faChevronLeft,
    faChevronRight,
    faCrown,
    faDatabase,
    faBolt,
    faShieldAlt,
    faNetworkWired,
    faCube,
    faChevronDown,
    faFolder,
    faFolderOpen,
    faCode
} from '@fortawesome/free-solid-svg-icons';
import * as SolidIcons from '@fortawesome/free-solid-svg-icons';
import TuFilaLogo from '../resources/TuFilaLogo';

import { Link } from 'react-router-dom';

/**
 * SidebarItem — Recursive component to render menu items and their children.
 */
const SidebarItem = ({ item, level = 0, activeTab, onTabChange, isCollapsed }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasChildren = (item.items && item.items.length > 0) || (item.menus && item.menus.length > 0);

    // Normalize ID/Path for navigation
    // If it's the index, we use an empty string.
    const itemPath = (item.route || item.ModuleRoute || item.id || '').replace(/^\/+/, '');
    const isActive = (activeTab === itemPath) || (activeTab === '' && itemPath === '');

    // Combine menus and items if they exist separately
    const children = [...(item.menus || []), ...(item.items || [])];

    const toggleOpen = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    // If it's a folder, it just toggles. If it's a link, it navigates.
    const ItemWrapper = hasChildren ? 'div' : Link;
    const wrapperProps = hasChildren ? {
        onClick: toggleOpen
    } : {
        to: `/Turnero/SuperAdmin/${itemPath}`,
        onClick: (e) => {
            // Log navigation as requested by user
            console.log(`Navegando a: ${itemPath}`, item.label);
        }
    };

    return (
        <div className="sa-nav-node">
            <ItemWrapper
                className={`sa-nav-item ${isActive ? 'active' : ''} ${hasChildren ? 'has-children' : ''}`}
                title={isCollapsed ? item.label : ''}
                style={{
                    paddingLeft: isCollapsed ? '1.1rem' : `${1.1 + level * 0.8}rem`,
                    textDecoration: 'none' // For Link
                }}
                {...wrapperProps}
            >
                <span className="sa-nav-icon">
                    <FontAwesomeIcon icon={item.icon || (hasChildren ? (isOpen ? faFolderOpen : faFolder) : faCube)} />
                </span>
                {!isCollapsed && (
                    <>
                        <motion.span
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            style={{ flex: 1 }}
                        >
                            {item.label}
                        </motion.span>
                        {hasChildren && (
                            <motion.span
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                style={{ fontSize: '0.7rem', opacity: 0.5 }}
                                onClick={toggleOpen}
                            >
                                <FontAwesomeIcon icon={faChevronDown} />
                            </motion.span>
                        )}
                        {item.badge && (
                            <span className="sa-nav-badge">{item.badge}</span>
                        )}
                    </>
                )}
            </ItemWrapper>

            <AnimatePresence>
                {hasChildren && isOpen && !isCollapsed && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                        style={{ overflow: 'hidden', borderLeft: '1px solid rgba(168, 85, 247, 0.1)', marginLeft: '1.8rem' }}
                    >
                        {children.map((child, idx) => (
                            <SidebarItem
                                key={child.id || `sub-${level}-${idx}`}
                                item={child}
                                level={level + 1}
                                activeTab={activeTab}
                                onTabChange={onTabChange}
                                isCollapsed={isCollapsed}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

/**
 * SuperAdminSidebar — Platform-level navigation for the Super Admin panel.
 * Uses purple accent to visually differentiate from company admin.
 */
const SuperAdminSidebar = ({ activeTab, onTabChange, isCollapsed, toggleCollapse, dynamicMenu = [] }) => {

    const mapIcon = (iconName) => {
        if (!iconName) return null;
        if (typeof iconName !== 'string') return iconName;
        return SolidIcons[iconName] || faCube;
    };

    const staticGroups = [
        {
            title: 'Resumen',
            items: [
                { id: '', label: 'Dashboard Global', icon: faChartLine },
            ]
        },
        {
            title: 'Plataforma',
            items: [
                { id: 'admins', label: 'Administradores', icon: faUserTie },
                { id: 'companies', label: 'Empresas', icon: faBuilding },
                { id: 'branches', label: 'Sucursales', icon: faStore },
                { id: 'staff', label: 'Usuarios / Staff', icon: faUsers },
            ]
        },
        {
            title: 'Herramientas',
            items: [
                { id: 'querybuilder', label: 'Query Builder', icon: faDatabase },
                { id: 'menubuilder', label: 'Menu Builder', icon: faBolt },
                { id: 'apiportal', label: 'Portal de APIs', icon: faCode },
            ]
        },
    ];

    // Recursive mapper to handle any depth in dynamic menu
    const mapDynamicItem = (item) => ({
        id: item.id || item.ModuleKey || item.GroupId,
        label: item.label || item.GroupLabel || item.ModuleLabel,
        icon: mapIcon(item.icon || item.GroupIcon || item.ModuleIcon),
        type: item.type,
        // Support both nesting patterns
        items: (item.items || []).map(mapDynamicItem),
        menus: (item.menus || []).map(mapDynamicItem),
        badge: item.badge
    });

    // Merge static and dynamic menu
    const menuGroups = [...staticGroups];

    // 1. Build a map for quick access and initialize children arrays
    const groupMap = {};

    // Flatten and identify all groups
    dynamicMenu.forEach(g => {
        // Try to find GroupId at top level or from first item (defensive against pre-grouped backend)
        const gId = String(g.GroupId || g.id || (g.items && g.items[0]?.GroupId) || (g.items && g.items[0]?.ModuleGroupId) || '');
        const pId = g.ParentGroupId || (g.items && g.items[0]?.ParentGroupId) || null;

        if (gId && gId !== 'undefined' && gId !== '') {
            groupMap[gId] = {
                ...g,
                id: gId,
                ParentGroupId: pId,
                label: g.GroupLabel || g.label || g.title,
                menus: g.menus || [],
                items: g.items || []
            };
        } else {
            // If we can't identify a GroupId, treat it as a direct section (fallback)
            menuGroups.push({
                title: g.GroupLabel || g.label || g.title,
                items: (g.items || []).map(mapDynamicItem),
                menus: (g.menus || []).map(mapDynamicItem)
            });
        }
    });

    // 2. Assemble the tree and identify root groups
    const dynamicRootGroups = [];
    Object.values(groupMap).forEach(g => {
        const pId = g.ParentGroupId ? String(g.ParentGroupId) : null;
        if (pId && groupMap[pId]) {
            groupMap[pId].menus.push(g);
        } else {
            dynamicRootGroups.push(g);
        }
    });

    // 3. Add identified root groups to the sidebar sections
    dynamicRootGroups.forEach(group => {
        const hasContent = (group.items && group.items.length > 0) || (group.menus && group.menus.length > 0);
        if (hasContent) {
            menuGroups.push({
                title: group.label,
                items: (group.items || []).map(mapDynamicItem),
                menus: (group.menus || []).map(mapDynamicItem)
            });
        }
    });

    return (
        <motion.div
            className="sa-sidebar"
            initial={false}
            animate={{ width: isCollapsed ? 84 : 290 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
        >
            {/* Header */}
            <div className="sa-sidebar-header">
                <motion.div
                    initial={false}
                    animate={{ scale: isCollapsed ? 0.45 : 1 }}
                    className="d-flex flex-column align-items-center"
                >
                    <TuFilaLogo size={isCollapsed ? 80 : 120} />
                    {!isCollapsed && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="d-flex flex-column align-items-center gap-1 mt-2"
                        >
                            <div className="sa-brand-badge">
                                <FontAwesomeIcon icon={faCrown} style={{ fontSize: '0.6rem' }} />
                                Super Admin
                            </div>
                            <div className="sa-brand-title">TuFila</div>
                        </motion.div>
                    )}
                </motion.div>

                <button
                    className="sa-collapse-btn"
                    onClick={toggleCollapse}
                    title={isCollapsed ? 'Expandir' : 'Colapsar'}
                >
                    <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
                </button>
            </div>

            {/* Navigation */}
            <nav className="sa-nav">
                {menuGroups.map((group, gIdx) => (
                    <div key={gIdx} className="mb-4">
                        {!isCollapsed && (
                            <div className="sa-group-label">{group.title}</div>
                        )}
                        <div className="d-flex flex-column" style={{ gap: '2px' }}>
                            {[...(group.menus || []), ...(group.items || [])].map((item, iIdx) => (
                                <SidebarItem
                                    key={item.id || `g${gIdx}-i${iIdx}`}
                                    item={item}
                                    activeTab={activeTab}
                                    onTabChange={onTabChange}
                                    isCollapsed={isCollapsed}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Footer */}
            <div className="sa-sidebar-footer">
                {!isCollapsed ? (
                    <div className="d-flex flex-column align-items-center gap-1">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#334155', fontSize: '0.65rem' }}>
                            <FontAwesomeIcon icon={faShieldAlt} />
                            <span>OctoCore Engine</span>
                        </div>
                        <small style={{ color: 'var(--sa-primary)', fontWeight: 700, fontSize: '0.7rem' }}>v2.1 Platform</small>
                    </div>
                ) : (
                    <FontAwesomeIcon icon={faShieldAlt} style={{ color: 'var(--sa-primary)', fontSize: '0.8rem' }} />
                )}
            </div>
        </motion.div>
    );
};

export default SuperAdminSidebar;

